import {useState, useEffect, useRef} from 'react';
import cx from 'classnames';
import {htmlSerialize} from '@baidu/realtime-editor';

import {getMorphoService, serviceNames} from '@/rte/editor';
import * as store from '@/store';
import ErrorImg from '@/assets/img/error.png';
import Loading from '@/assets/img/loading.gif';
import {ServerConfig} from '@/config/routers';

import styles from './index.module.scss';

interface IOptions {
    disableSecondLoad?: boolean; // 是否屏蔽二次加载
    disableDragImage?: boolean; // 是否屏蔽拖拽图片
    docGuid?: string; // 自定义文档 ID，若没传则默认使用当前文档 ID
    serializeConfig?: {
        useCover?: {
            margin: string;
            box: number[];
        };
    };
}

// 文档指定节点预览类（静态化）
export class DocNodePreviewClass {
    jsonContent: any[] = [];
    options: IOptions = {
        disableSecondLoad: false,
        disableDragImage: false,
    };
    docGuid: string;
    docToken: string;
    htmlSerializeUtils: htmlSerialize | undefined;
    diagramMap: Map<string, string>;
    mindMap: Map<string, string>;

    constructor(jsonContent: any[], options?: IOptions) {
        this.jsonContent = jsonContent;
        this.diagramMap = new Map();
        this.mindMap = new Map();

        if (options?.disableSecondLoad) {
            this.options.disableSecondLoad = true;
        }

        if (options?.disableDragImage) {
            this.options.disableDragImage = true;
        }

        if (options?.docGuid) {
            this.docGuid = options.docGuid;
        } else {
            const {docGuid} = store.getData(store.KEYS.CUR_DOCUMENT) || {};
            this.docGuid = docGuid;
        }

        if (options?.serializeConfig) {
            this.options.serializeConfig = options?.serializeConfig;
        }

        const {accessToken: rteToken} = store.getData(store.KEYS.CUR_DOCUMENT_TOKEN) || {};
        this.docToken = rteToken;
    }

    /**
     * 获取序列化后的HTML字符串
     *
     * @returns 返回序列化后的HTML字符串
     */
    async getSerializeHtml() {
        const serialize = getMorphoService(serviceNames.IHtmlSerializerService)?.serialize;

        if (!serialize) {
            throw new Error('[DocNodePreview] serialize is undefined');
        }

        if (!this.docToken) {
            throw new Error('[DocNodePreview] token is undefined');
        }

        const serializeOptions: any = {
            inlineStyle: true, // 序列化时是否采用样式内联的方式进行
            addImageQueries: (src: string): string => {
                if (!/http(s)?:\/\/([a-z-0-9]+\.)+baidu(-int)?.com\.*/.test(src)) {
                    return src;
                }

                const urlObj = new URL(src);
                urlObj.searchParams.set('sign', this.docToken);
                return urlObj.toString();
            },
            inheritFontSize: true, // 是否继承父节点的字体大小
            ...this.options.serializeConfig,
        };

        const themeColor = getMorphoService(serviceNames.ICoverService)?.getThemeColor();
        if (themeColor) {
            serializeOptions.theme = themeColor;
        }


        if (this.options.disableDragImage) {
            serializeOptions.imageDraggable = false;
        }

        if (this.options.disableSecondLoad) {
            // 屏蔽二次加载，则二次加载元素是否使用占位符
            serializeOptions.placeholderImage = true;
        } else {
            if (!this.docGuid) {
                throw new Error('[DocNodePreview] docGuid is undefined');
            }

            this.htmlSerializeUtils = new htmlSerialize({
                appId: '1',
                docId: this.docGuid,
                appToken: this.docToken,
                entrypoint: ServerConfig.rte,
            });

            await this.getDiagramData(this.jsonContent);
            await this.getMindMapData(this.jsonContent);
            await this.getOnlineTodoData(this.jsonContent);

            serializeOptions.getDiagramContentByIdSync = (id: string) => this.diagramMap.get(id);
            serializeOptions.getMindmapBase64DataByIdSync = (id: string) => this.mindMap.get(id);
        }

        const html = serialize(this.jsonContent, serializeOptions);

        return html;
    }

    /**
    * 获取流程图数据
    *
    * @param jsonContent 文档内容
    * @returns 流程图数据
    */
    private async getDiagramData(jsonContent: any) {
        const diagramUtils = getMorphoService(serviceNames.IDiagramService);

        if (
            diagramUtils
            && diagramUtils.getDiagramIds
            && diagramUtils.asyncLoadDiagramJs
            && this.htmlSerializeUtils
        ) {
            // 内容是否存在流程图
            const diagramIds = diagramUtils.getDiagramIds(jsonContent);

            if (Array.isArray(diagramIds) && diagramIds.length) {
                // 批量获取流程图 content
                const diagramPromiseList = diagramIds.map(id => this.htmlSerializeUtils?.getDiagram(id));

                const [_, ...diagramContentList] = await Promise.all([
                    diagramUtils.asyncLoadDiagramJs(), // 异步加载流程图渲染所需的 js 资源
                    ...diagramPromiseList,
                ]);

                diagramContentList.forEach(diagramResult => {
                    const {id, content} = diagramResult || {};
                    this.diagramMap.set(id, content);
                });
            }
        }
    }

    /**
     * 获取思维导图数据
     *
     * @param jsonContent 文档内容
     * @returns 思维导图数据
     */
    private async getMindMapData(jsonContent: any) {
        const mindMapUtils = getMorphoService(serviceNames.IMindmapService);

        if (
            mindMapUtils
                && mindMapUtils.asyncLoadMindmapJs
                && mindMapUtils.getMindmapIdsByNodes
                && mindMapUtils.getBase64DataByMindmapContentAsync
                && this.htmlSerializeUtils
        ) {
            // 内容是否存在思维导图
            const mindMapIds = mindMapUtils.getMindmapIdsByNodes(jsonContent);

            if (Array.isArray(mindMapIds) && mindMapIds.length) {
                // 批量获取思维导图 content
                const mindMapPromiseList = mindMapIds.map(id => this.htmlSerializeUtils?.getMindMap(id));

                const [_, ...mindMapContentList] = await Promise.all([
                    mindMapUtils.asyncLoadMindmapJs(), // 异步加载思维导图渲染所需的 js 资源
                    ...mindMapPromiseList,
                ]);

                // 将思维导图 content 进行异步 layout 操作，获得 SVG 数据(Base64格式)
                const svgList = await mindMapUtils.getBase64DataByMindmapContentAsync(
                    mindMapContentList.map(item => ({
                        id: item?.id,
                        content: item?.content,
                    }))
                );

                svgList.forEach((svg: {id: string, base64: string, content: any}) => {
                    const {id, base64} = svg;
                    this.mindMap.set(id, base64);
                });
            }
        }
    }

    /**
     * 获取如流待办数据
     *
     * @param jsonContent 文档内容
     */
    private async getOnlineTodoData(jsonContent: any) {
        const onlineTodoListUtils = getMorphoService(serviceNames.IOnlineTodoListService);

        if (
            onlineTodoListUtils
                && onlineTodoListUtils.getOnlineTodoIdsByNodes
                && onlineTodoListUtils.replaceOnlineTodoNode
                && this.htmlSerializeUtils
        ) {
            const todoIds = onlineTodoListUtils.getOnlineTodoIdsByNodes(jsonContent);

            if (todoIds && Object.keys(todoIds).length) {
                const {todos} = await this.htmlSerializeUtils?.getOnlineTodo(todoIds);

                if (todos && Object.keys(todos).length) {
                    onlineTodoListUtils.replaceOnlineTodoNode(jsonContent, todos);
                }
            }
        }
    }
}

export enum Status {
    Success ='success',
    Loading = 'loading',
    Error = 'error',
}

interface IProps {
    content: any[];
    disableSecondLoad?: boolean; // 是否屏蔽二次加载，默认 false
    disableDragImage?: boolean; // 是否屏蔽拖拽图片，默认 false
    docGuid?: string; // 自定义文档 ID，若没传则默认使用当前文档 ID
    className?: string;
    serializeConfig?: {
        useCover?: {
            margin: string;
            box: number[];
        };
    };
    onStatusChange?: (status: Status) => void; // 状态改变回调，通知父组件当前状态
}

// 文档指定节点预览组件（静态化）
export const DocNodePreview: React.FC<IProps> = ({
    content = [],
    disableSecondLoad = false,
    disableDragImage = false,
    docGuid,
    className,
    serializeConfig,
    onStatusChange,
}) => {
    const [html, setHtml] = useState('');
    const [status, setStatus] = useState<Status>(Status.Success);
    const domRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        async function getDocContent() {
            try {
                setStatus(Status.Loading);
                const instance = new DocNodePreviewClass(
                    content,
                    {disableSecondLoad, disableDragImage, docGuid, serializeConfig}
                );
                const html = await instance.getSerializeHtml();

                setHtml(html);
                setStatus(Status.Success);

                try {
                    setTimeout(() => {
                        // editorStateService为了拿到MorphoAPI
                        const editorStateService = getMorphoService(serviceNames.IEditorStateService);
                        if (editorStateService) {
                            const {MorphoAPI} = editorStateService?.editor || {};
                            requestAnimationFrame(() => MorphoAPI.processStaticDOMs(editorStateService?.editor, domRef.current));
                        }
                    }, 0);
                } catch (err) {
                    console.error(err);
                }
            } catch (err) {
                console.error(err);
                setStatus(Status.Error);
            }
        }

        getDocContent();
    }, [
        content,
        disableSecondLoad,
        disableDragImage,
        docGuid,
        serializeConfig,
    ]);

    useEffect(() => {
        if (onStatusChange) {
            onStatusChange(status);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status]);

    return (
        status === Status.Loading
            ? (
                <div className={styles.loading}>
                    <img
                        draggable={false}
                        src={Loading}
                        alt=""
                    />
                </div>
            )
            : status === Status.Success
            /* bca-disable */
            // eslint-disable-next-line react/no-danger
                ? <div ref={domRef} className={cx(styles.main, className)} dangerouslySetInnerHTML={{__html: html}} />
                : (
                    <div className={styles.error}>
                        <img
                            draggable={false}
                            src={ErrorImg}
                            alt=""
                        />
                        <div className={styles.text}>加载异常，请重试</div>
                    </div>
                )
    );
};
